import React from 'react';
import '../../css/about.css';

const PrivacyPolicy = () => {
  const lastUpdated = 'June 16, 2026';

  return (
    <div className="about-page">
      <section className="about-section">
        <div className="container" style={{ maxWidth: '760px' }}>
          <h1>Privacy Policy</h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Last updated: {lastUpdated}
          </p>

          <p>
            UnsupervisedBias ("we", "us") operates this website and affiliated products including{' '}
            <a href="https://pdubfantasy.com" style={{ color: 'var(--pink)' }}>PdubFantasy</a>. This
            policy explains what personal data we collect, how we use it, and your rights. We are
            based in Washington, DC, USA.
          </p>

          <h2>Information we collect</h2>
          <ul>
            <li>
              <strong>Account information:</strong> When you create an account, we store your email
              address, display name, and — if you set one — a securely hashed password. We never
              store passwords in plain text.
            </li>
            <li>
              <strong>Sign-in via Google:</strong> If you sign in with Google, we receive your
              verified email address, name, and profile picture from Google. We do not receive your
              Google password. See the Google Sign-In section below for full details.
            </li>
            <li>
              <strong>User-generated content:</strong> Blog comments, profile information, and other
              content you submit through our platforms.
            </li>
            <li>
              <strong>Technical data:</strong> Standard server logs (IP address, browser type,
              request timestamps) used for security and debugging. Logs are rotated on a regular
              schedule.
            </li>
          </ul>

          <h2>How we use your information</h2>
          <ul>
            <li>To provide and operate our services.</li>
            <li>To authenticate you and keep your account secure.</li>
            <li>
              To send transactional emails (verification codes, password resets). We do not send
              marketing emails unless you opt in.
            </li>
            <li>To improve our products and understand how features are used.</li>
          </ul>

          <h2>Tracking and advertising</h2>
          <p>
            <strong>We do not use advertising tracking cookies.</strong> UnsupervisedBias does not
            run Facebook pixel, Google Ads remarketing tags, or any other third-party behavioral
            advertising trackers. The only cookies and browser storage we set are strictly necessary
            for authentication and session management (keeping you signed in).
          </p>

          <h2>How we share your information</h2>
          <p>
            We do not sell or rent your personal information. We share data only with service
            providers that help us operate (hosting, email delivery) and only as much as needed.
            We may disclose information if required by law.
          </p>

          <h2>Your rights and choices</h2>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
            <li>
              <strong>Correction:</strong> Update your display name and email from your Account
              Settings at any time.
            </li>
            <li>
              <strong>Deletion:</strong> Delete your account and all associated personal data from
              Account Settings, or email{' '}
              <a href="mailto:support@unsupervisedbias.com" style={{ color: 'var(--pink)' }}>
                support@unsupervisedbias.com
              </a>{' '}
              with the subject "Delete My Account".
            </li>
            <li>
              <strong>Data portability:</strong> Request a machine-readable export of your data by
              emailing us with the subject "Data Export Request". We will respond within 30 days.
            </li>
            <li>
              <strong>Opt out of marketing:</strong> Manage email preferences from your Account
              Settings, or click "Unsubscribe" in any marketing email.
            </li>
          </ul>

          <h2>Data retention</h2>
          <p>
            We retain account data for as long as your account is active. When you delete your
            account, we remove personal data promptly. Aggregated, non-personal data may be
            retained for analytical purposes.
          </p>

          <h2>Google Sign-In — User Data Disclosure</h2>
          <p>When you sign in with Google, we access:</p>
          <ul>
            <li>
              <strong>Email address:</strong> Used to create your account and for account-related
              communications.
            </li>
            <li>
              <strong>Name:</strong> Used as your initial display name, which you can change at any
              time in Settings.
            </li>
            <li>
              <strong>Profile picture:</strong> Displayed on your profile. You can replace it with a
              custom image.
            </li>
          </ul>
          <p>
            <strong>How we use Google user data:</strong> Solely to authenticate you and create
            your account. We do not use Google user data for advertising, share it with third
            parties beyond what is needed to operate the service, or use it to train AI or machine
            learning models.
          </p>
          <p>
            <strong>Revoking access:</strong> You can revoke UnsupervisedBias's access to your
            Google account at any time via your{' '}
            <a
              href="https://myaccount.google.com/permissions"
              style={{ color: 'var(--pink)' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Account Permissions
            </a>{' '}
            page. Deleting your UnsupervisedBias account does not revoke the Google-level permission;
            you must do that separately.
          </p>
          <p>
            For Google's own privacy practices, see the{' '}
            <a
              href="https://policies.google.com/privacy"
              style={{ color: 'var(--pink)' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Privacy Policy
            </a>.
          </p>

          <h2>Meta / Instagram Integration</h2>
          <p>
            Our services integrate with Meta Platforms via the Instagram Graph API. This integration
            is used solely for content planning and analytics related to our own Instagram account
            (<strong>@unsupervisedbias</strong>). We may collect and process:
          </p>
          <ul>
            <li>Post content, captions, and media metadata (our own posts only)</li>
            <li>Engagement metrics: likes, comments, reach, and impressions (our own account)</li>
            <li>Account-level insights: follower count and profile views</li>
          </ul>
          <p>
            We do not collect Instagram data from end users of our websites. This data is used
            internally for content scheduling and is not sold or shared with third parties. For
            Meta's privacy practices, see the{' '}
            <a
              href="https://www.facebook.com/privacy/policy/"
              style={{ color: 'var(--pink)' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Meta Privacy Policy
            </a>.
            To request deletion of any data connected to Meta services, see our{' '}
            <a href="/data-deletion" style={{ color: 'var(--pink)' }}>Data Deletion page</a>.
          </p>

          <h2>GDPR — EU/EEA residents</h2>
          <p>
            If you are located in the EU or EEA, you have additional rights under the General Data
            Protection Regulation:
          </p>
          <ul>
            <li>
              <strong>Lawful basis:</strong> We process your data on the basis of (a) contractual
              necessity — to provide the service; (b) legitimate interests — security and product
              improvement; and (c) consent — where you have explicitly opted in.
            </li>
            <li>
              <strong>Right to lodge a complaint:</strong> You may file a complaint with your local
              data protection authority.
            </li>
            <li>
              <strong>International transfers:</strong> Our servers are located in the United States.
              By using our services from the EU/EEA, you acknowledge that your data may be
              transferred to and processed in the US. We rely on Standard Contractual Clauses where
              applicable.
            </li>
            <li>
              <strong>Data controller:</strong> UnsupervisedBias, Washington, DC —{' '}
              <a href="mailto:support@unsupervisedbias.com" style={{ color: 'var(--pink)' }}>
                support@unsupervisedbias.com
              </a>
            </li>
          </ul>

          <h2>CCPA — California residents</h2>
          <p>
            California residents have the right to know what personal information we collect, request
            deletion, and opt out of the sale of personal information. <strong>We do not sell your
            personal information.</strong> To exercise CCPA rights, contact us at{' '}
            <a href="mailto:support@unsupervisedbias.com" style={{ color: 'var(--pink)' }}>
              support@unsupervisedbias.com
            </a>. We will respond within 45 days.
          </p>

          <h2>Cookies and browser storage</h2>
          <p>
            We use browser local storage to keep you signed in (storing session tokens). We do not
            use third-party advertising cookies. Google Sign-In may set cookies on{' '}
            <code>accounts.google.com</code> during authentication; those are governed by Google's
            Privacy Policy. You can clear storage at any time in your browser settings, which will
            sign you out.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be communicated via
            a site notice or email. The "Last updated" date at the top reflects the most recent
            revision.
          </p>

          <h2>Contact</h2>
          <p>
            Questions or data requests:{' '}
            <a href="mailto:support@unsupervisedbias.com" style={{ color: 'var(--pink)' }}>
              support@unsupervisedbias.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
